import { useCallback, useRef, useState } from 'react';
import type { LayoutResult, NodeBounds } from '@/layout/types';
import type { MindMapNode } from '@/model/types';
import type { MindMapDocument } from '@/model/document';

/** Minimum mouse movement (in pixels) before a drag starts. */
const DRAG_THRESHOLD = 5;

interface DragState {
  nodeId: string;
  startClientX: number;
  startClientY: number;
  thresholdMet: boolean;
  /** If true, this is a free-position drag (Alt held). Otherwise it's reparenting. */
  isFreePosition: boolean;
}

interface UseDragNodeResult {
  isDragging: boolean;
  dragNodeId: string | null;
  dropTargetId: string | null;
  ghostPosition: { x: number; y: number } | null;
  /** True when Alt+dragging to reposition (not reparent). */
  isFreePositioning: boolean;
  onNodeMouseDown: (nodeId: string, e: React.MouseEvent) => void;
  onCanvasMouseMove: (e: React.MouseEvent) => void;
  onCanvasMouseUp: () => void;
  onNodeTouchStart: (nodeId: string, e: React.TouchEvent) => void;
  onCanvasTouchMove: (e: React.TouchEvent) => void;
  onCanvasTouchEnd: () => void;
}

function clientToSvg(
  clientX: number,
  clientY: number,
  svgEl: SVGSVGElement,
  panX: number,
  panY: number,
  zoom: number,
): { x: number; y: number } {
  const rect = svgEl.getBoundingClientRect();
  const svgX = clientX - rect.left;
  const svgY = clientY - rect.top;
  return {
    x: (svgX - panX) / zoom,
    y: (svgY - panY) / zoom,
  };
}

function findDropTarget(
  svgPoint: { x: number; y: number },
  layout: LayoutResult,
  dragNodeId: string,
  nodes: Map<string, MindMapNode>,
): string | null {
  const descendants = collectDescendants(dragNodeId, nodes);
  descendants.add(dragNodeId);

  let closestId: string | null = null;
  let closestDist = Infinity;

  for (const [id, bounds] of layout.nodeBounds) {
    if (descendants.has(id)) continue;
    const dist = distanceToBounds(svgPoint, bounds);
    if (dist < closestDist) {
      closestDist = dist;
      closestId = id;
    }
  }

  if (closestDist > 80) return null;
  return closestId;
}

function collectDescendants(
  nodeId: string,
  nodes: Map<string, MindMapNode>,
): Set<string> {
  const result = new Set<string>();
  const node = nodes.get(nodeId);
  if (!node) return result;
  for (const childId of node.childIds) {
    result.add(childId);
    for (const desc of collectDescendants(childId, nodes)) {
      result.add(desc);
    }
  }
  return result;
}

function distanceToBounds(
  point: { x: number; y: number },
  bounds: NodeBounds,
): number {
  const cx = Math.max(bounds.x, Math.min(point.x, bounds.x + bounds.width));
  const cy = Math.max(bounds.y, Math.min(point.y, bounds.y + bounds.height));
  const dx = point.x - cx;
  const dy = point.y - cy;
  return Math.sqrt(dx * dx + dy * dy);
}

export function useDragNode(
  svgRef: React.RefObject<SVGSVGElement | null>,
  layout: LayoutResult,
  nodes: Map<string, MindMapNode>,
  rootNodeId: string,
  mapDoc: MindMapDocument,
  panX: number,
  panY: number,
  zoom: number,
): UseDragNodeResult {
  const [isDragging, setIsDragging] = useState(false);
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{ x: number; y: number } | null>(null);
  const [isFreePositioning, setIsFreePositioning] = useState(false);

  const dragStateRef = useRef<DragState | null>(null);

  // Shared internal logic: start drag
  const handleDragStart = useCallback(
    (nodeId: string, clientX: number, clientY: number, altKey: boolean) => {
      if (nodeId === rootNodeId) {
        // Root can only be free-positioned (with Alt)
        if (!altKey) return;
      }

      dragStateRef.current = {
        nodeId,
        startClientX: clientX,
        startClientY: clientY,
        thresholdMet: false,
        isFreePosition: altKey,
      };
    },
    [rootNodeId],
  );

  // Shared internal logic: drag move
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      const ds = dragStateRef.current;
      if (!ds) return;

      const dx = clientX - ds.startClientX;
      const dy = clientY - ds.startClientY;

      if (!ds.thresholdMet) {
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        ds.thresholdMet = true;
        setIsDragging(true);
        setDragNodeId(ds.nodeId);
        setIsFreePositioning(ds.isFreePosition);
      }

      const svg = svgRef.current;
      if (!svg) return;

      const svgPoint = clientToSvg(clientX, clientY, svg, panX, panY, zoom);
      setGhostPosition(svgPoint);

      if (!ds.isFreePosition) {
        // Reparenting mode: find drop target
        const target = findDropTarget(svgPoint, layout, ds.nodeId, nodes);
        setDropTargetId(target);
      } else {
        // Free positioning mode: no drop target
        setDropTargetId(null);
      }
    },
    [svgRef, panX, panY, zoom, layout, nodes],
  );

  // Shared internal logic: drag end
  const handleDragEnd = useCallback(() => {
    const ds = dragStateRef.current;
    if (ds?.thresholdMet && dragNodeId) {
      if (ds.isFreePosition && ghostPosition) {
        // Alt+drag: set manual position
        mapDoc.setNodePosition(dragNodeId, ghostPosition.x, ghostPosition.y);
      } else if (dropTargetId) {
        // Normal drag: reparent
        mapDoc.moveNode(dragNodeId, dropTargetId);
      }
    }

    dragStateRef.current = null;
    setIsDragging(false);
    setDragNodeId(null);
    setDropTargetId(null);
    setGhostPosition(null);
    setIsFreePositioning(false);
  }, [dragNodeId, dropTargetId, ghostPosition, mapDoc]);

  // Mouse handlers
  const onNodeMouseDown = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      handleDragStart(nodeId, e.clientX, e.clientY, e.altKey);
    },
    [handleDragStart],
  );

  const onCanvasMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleDragMove(e.clientX, e.clientY);
    },
    [handleDragMove],
  );

  const onCanvasMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch handlers
  const onNodeTouchStart = useCallback(
    (nodeId: string, e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      handleDragStart(nodeId, touch.clientX, touch.clientY, false);
    },
    [handleDragStart],
  );

  const onCanvasTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // Only process single-finger touches (ignore multi-touch / pinch-to-zoom)
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      handleDragMove(touch.clientX, touch.clientY);
    },
    [handleDragMove],
  );

  const onCanvasTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  return {
    isDragging,
    dragNodeId,
    dropTargetId,
    ghostPosition,
    isFreePositioning,
    onNodeMouseDown,
    onCanvasMouseMove,
    onCanvasMouseUp,
    onNodeTouchStart,
    onCanvasTouchMove,
    onCanvasTouchEnd,
  };
}
